import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useFrame, useThree} from "@react-three/fiber";
import * as THREE from "three";
import {cloneAxes} from "./orientationAxes";
import {normalizeLocalRotationAngles} from "./localRotation";
import {
    applyFreeRotationDragStep,
    createFreeRotationDragState,
} from "./freeRotationDrag";
import {getInstanceLocalCornersM, getPanelRotationGripPointsM} from "./snapPlacement";

const HANDLE_COLOR = "#f9a825";
const HANDLE_HOVER = "#ffeb3b";
/** Базовая точка (начало направляющих осей). */
const BASE_COLOR = "#1565c0";
const BASE_HOVER = "#42a5f5";
const BASE_PRESSED = "#ff6f00";

const isOriginCorner = (pos) => (
    Math.abs(pos?.[0] || 0) < 1e-6
    && Math.abs(pos?.[1] || 0) < 1e-6
    && Math.abs(pos?.[2] || 0) < 1e-6
);

/**
 * Ручки на всех углах выбранной детали: тянуть можно за любой угол, не только за origin.
 * Базовая точка (начало осей) — отдельная сфера с иной подсветкой при нажатии.
 * Перемещение — в плоскости экрана (как pan), вся деталь едет целиком.
 * При orthoEnabled — сдвиг только вдоль одной мировой оси X/Y/Z (без диагоналей).
 * rotationWorldAxis (x|y|z) + жёлтые углы — вращение вокруг мировой оси (свободный поворот).
 */
const DetailCornerHandles = ({
    inst,
    enabled,
    groupRef,
    /** Группа с базисом детали (если поза разделена на перенос + поворот). */
    orientationGroupRef = null,
    onDragStart,
    onDrag,
    onDragEnd,
    shrinkScale = 1,
    shrinkLocal = null,
    orthoEnabled = false,
    rotationWorldAxis = null,
    rotationReferenceAxes = null,
    rotationStartAngles = null,
    startAxes = null,
    faceFlip = false,
    /** Поверх оболочки модуля (сцена композиции / встраиваемый модуль). */
    renderOnTop = false,
}) => {
    const {camera, gl, viewport} = useThree();
    const dragRef = useRef(null);
    const handlesGroupRef = useRef(null);
    const handleWorldPositionRef = useRef(new THREE.Vector3());
    const [hovered, setHovered] = useState(null);
    const [pressed, setPressed] = useState(null);
    const corners = useMemo(() => getInstanceLocalCornersM(inst), [inst]);
    const rotationGrips = useMemo(
        () => (rotationWorldAxis ? getPanelRotationGripPointsM(inst) : []),
        [inst, rotationWorldAxis],
    );

    const getOrientObject = useCallback(() => (
        orientationGroupRef?.current ?? groupRef.current
    ), [groupRef, orientationGroupRef]);

    const handleSize = useMemo(() => {
        let minSpan = Infinity;
        for (let i = 0; i < corners.length; i += 1) {
            for (let j = i + 1; j < corners.length; j += 1) {
                const dx = corners[i][0] - corners[j][0];
                const dy = corners[i][1] - corners[j][1];
                const dz = corners[i][2] - corners[j][2];
                const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
                if (d > 1e-6) minSpan = Math.min(minSpan, d);
            }
        }
        if (!Number.isFinite(minSpan)) return 0.012;
        return Math.min(0.014, Math.max(0.008, minSpan * 0.08));
    }, [corners]);

    const baseSize = handleSize * 1.35;
    const hasOriginCorner = corners.some(isOriginCorner);

    const handleMaterialProps = useMemo(() => (
        renderOnTop
            ? {depthTest: false, depthWrite: false, transparent: false}
            : {depthTest: true}
    ), [renderOnTop]);
    const handleRenderOrder = renderOnTop ? 14 : 0;

    const getGeomUnitSize = useCallback((mesh) => (
        mesh?.userData?.basePoint ? baseSize * 0.65 : handleSize
    ), [baseSize, handleSize]);

    useFrame(() => {
        const root = handlesGroupRef.current;
        if (!root || !enabled) return;

        const shrink = Number.isFinite(Number(shrinkScale)) ? Number(shrinkScale) : 1;
        root.children.forEach((child) => {
            if (!child.isMesh) return;
            child.getWorldPosition(handleWorldPositionRef.current);
            const currentViewport = viewport.getCurrentViewport(
                camera,
                handleWorldPositionRef.current,
            );
            const pixelsPerWorldUnit = Number(currentViewport?.factor);
            if (!Number.isFinite(pixelsPerWorldUnit) || pixelsPerWorldUnit <= 0) return;

            // Постоянный экранный размер: при зуме ручки уменьшаются (как у деталей).
            const targetWorldSize = 8 / pixelsPerWorldUnit;
            const geomUnitSize = getGeomUnitSize(child);
            const cameraScale = Math.min(1, Math.max(0.02, targetWorldSize / geomUnitSize));
            const pos = child.position;
            const isShrinkTarget = Array.isArray(shrinkLocal)
                && Math.abs(pos.x - shrinkLocal[0]) < 1e-6
                && Math.abs(pos.y - shrinkLocal[1]) < 1e-6
                && Math.abs(pos.z - shrinkLocal[2]) < 1e-6;
            child.scale.setScalar(cameraScale * (isShrinkTarget ? shrink : 1));
        });
    });

    useEffect(() => {
        if (!enabled) {
            dragRef.current = null;
            setPressed(null);
            return undefined;
        }

        const onMove = (event) => {
            const drag = dragRef.current;
            const obj = groupRef.current;
            const orientObj = getOrientObject();
            if (!drag || !obj) return;

            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1,
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, camera);
            const hit = new THREE.Vector3();
            if (!raycaster.ray.intersectPlane(drag.plane, hit)) return;

            if (drag.mode === "rotate") {
                const result = applyFreeRotationDragStep(drag, hit, camera);
                if (result) {
                    onDrag?.(result);
                }
                return;
            }

            const freeDelta = hit.sub(drag.startHit);
            if (drag.ortho) {
                // Орто: только одна мировая ось — по максимальной |Δ|, без «залипания»
                // на ось гизмо (углы не смотрят на TransformControls.axis).
                const ax = Math.abs(freeDelta.x);
                const ay = Math.abs(freeDelta.y);
                const az = Math.abs(freeDelta.z);
                if (ax >= ay && ax >= az) {
                    obj.position.set(
                        drag.startGroup.x + freeDelta.x,
                        drag.startGroup.y,
                        drag.startGroup.z,
                    );
                } else if (ay >= ax && ay >= az) {
                    obj.position.set(
                        drag.startGroup.x,
                        drag.startGroup.y + freeDelta.y,
                        drag.startGroup.z,
                    );
                } else {
                    obj.position.set(
                        drag.startGroup.x,
                        drag.startGroup.y,
                        drag.startGroup.z + freeDelta.z,
                    );
                }
            } else {
                obj.position.copy(drag.startGroup).add(freeDelta);
            }
            onDrag?.(drag.localPosition);
        };

        const onUp = () => {
            const drag = dragRef.current;
            if (!drag) return;
            dragRef.current = null;
            setPressed(null);
            onDragEnd?.({
                localPosition: drag.localPosition,
                axes: drag.mode === "rotate" ? drag.currentAxes : undefined,
                localRotationAngles: drag.mode === "rotate" ? drag.currentAngles : undefined,
            });
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };
    }, [
        camera,
        enabled,
        faceFlip,
        gl,
        getOrientObject,
        groupRef,
        orientationGroupRef,
        inst?.axes,
        onDrag,
        onDragEnd,
        rotationReferenceAxes,
        rotationStartAngles,
        rotationWorldAxis,
        startAxes,
    ]);

    if (!enabled) return null;

    const skipBasePointer = Boolean(rotationWorldAxis);

    const startDrag = (event, handleKey, localPosition, isBaseHandle) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        if (rotationWorldAxis) {
            if (isBaseHandle) {
                return;
            }
        }
        const obj = groupRef.current;
        const orientObj = getOrientObject();
        if (!obj) return;

        const camDir = new THREE.Vector3();
        camera.getWorldDirection(camDir);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, event.point);

        const useRotate = Boolean(rotationWorldAxis);
        const initialAxes = cloneAxes(startAxes || inst?.axes);
        const referenceAxes = cloneAxes(rotationReferenceAxes || initialAxes);
        const startAngles = normalizeLocalRotationAngles(rotationStartAngles);

        if (useRotate) {
            dragRef.current = {
                ...createFreeRotationDragState({
                    rotationAxisKey: rotationWorldAxis,
                    initialAxes,
                    referenceAxes,
                    startAngles,
                    faceFlip,
                    orientObj: orientObj || obj,
                    poseObj: obj,
                    hitWorld: event.point,
                }),
                plane,
                startGroup: obj.position.clone(),
                localPosition: [...localPosition],
                mode: "rotate",
            };
        } else {
            dragRef.current = {
                plane,
                startGroup: obj.position.clone(),
                startHit: event.point.clone(),
                localPosition: [...localPosition],
                ortho: Boolean(orthoEnabled),
                mode: "translate",
                faceFlip: Boolean(faceFlip),
            };
        }
        setPressed(handleKey);
        onDragStart?.(localPosition);
    };

    const resolveColor = (key, isBase) => {
        if (pressed === key) return isBase ? BASE_PRESSED : HANDLE_HOVER;
        if (hovered === key) return isBase ? BASE_HOVER : HANDLE_HOVER;
        return isBase ? BASE_COLOR : HANDLE_COLOR;
    };

    if (rotationWorldAxis && rotationGrips.length > 0) {
        return (
            <group ref={handlesGroupRef}>
                {rotationGrips.map((grip) => {
                    const isBase = grip.isBase;
                    const key = grip.key;
                    const pos = grip.pos;
                    const isFace = grip.kind === "face";
                    const isEdge = grip.kind === "edge";
                    const gripSize = isFace
                        ? handleSize * 1.15
                        : isEdge
                            ? handleSize * 0.95
                            : handleSize;
                    return (
                        <mesh
                            key={key}
                            position={pos}
                            renderOrder={handleRenderOrder}
                            userData={{module3dRole: "corner-handle", basePoint: isBase}}
                            raycast={skipBasePointer && isBase ? () => null : undefined}
                            onPointerDown={(e) => startDrag(e, key, pos, isBase)}
                            onPointerOver={(e) => {
                                e.stopPropagation();
                                setHovered(key);
                            }}
                            onPointerOut={() => setHovered((prev) => (prev === key ? null : prev))}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {isFace
                                ? <boxGeometry args={[gripSize * 1.6, gripSize * 1.6, gripSize * 0.35]}/>
                                : isEdge
                                    ? <boxGeometry args={[gripSize, gripSize * 2.2, gripSize]}/>
                                    : <boxGeometry args={[gripSize, gripSize, gripSize]}/>}
                            <meshStandardMaterial
                                color={resolveColor(key, isBase)}
                                roughness={isBase ? 0.35 : 0.45}
                                metalness={isBase ? 0.2 : 0.1}
                                transparent={isFace}
                                opacity={isFace ? 0.55 : 1}
                                {...handleMaterialProps}
                            />
                        </mesh>
                    );
                })}
            </group>
        );
    }

    return (
        <group ref={handlesGroupRef}>
            {/* Базовая точка — начало направляющих осей (если её ещё нет среди углов). */}
            {!hasOriginCorner && (
                <mesh
                    position={[0, 0, 0]}
                    renderOrder={handleRenderOrder}
                    userData={{module3dRole: "corner-handle", basePoint: true}}
                    raycast={skipBasePointer ? () => null : undefined}
                    onPointerDown={(e) => startDrag(e, "base", [0, 0, 0], true)}
                    onPointerOver={(e) => {
                        e.stopPropagation();
                        setHovered("base");
                    }}
                    onPointerOut={() => setHovered((prev) => (prev === "base" ? null : prev))}
                    onClick={(e) => e.stopPropagation()}
                >
                    <sphereGeometry args={[baseSize * 0.65, 16, 16]}/>
                    <meshStandardMaterial
                        color={resolveColor("base", true)}
                        roughness={0.35}
                        metalness={0.2}
                        emissive={pressed === "base" ? BASE_PRESSED : "#000000"}
                        emissiveIntensity={pressed === "base" ? 0.45 : 0}
                        {...handleMaterialProps}
                    />
                </mesh>
            )}
            {corners.map((pos, index) => {
                const isBase = isOriginCorner(pos);
                const key = isBase ? "base" : index;
                return (
                    <mesh
                        key={index}
                        position={pos}
                        renderOrder={handleRenderOrder}
                        userData={{module3dRole: "corner-handle", basePoint: isBase}}
                        raycast={skipBasePointer && isBase ? () => null : undefined}
                        onPointerDown={(e) => startDrag(e, key, pos, isBase)}
                        onPointerOver={(e) => {
                            e.stopPropagation();
                            setHovered(key);
                        }}
                        onPointerOut={() => setHovered((prev) => (prev === key ? null : prev))}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isBase
                            ? <sphereGeometry args={[baseSize * 0.65, 16, 16]}/>
                            : <boxGeometry args={[handleSize, handleSize, handleSize]}/>}
                        <meshStandardMaterial
                            color={resolveColor(key, isBase)}
                            roughness={isBase ? 0.35 : 0.45}
                            metalness={isBase ? 0.2 : 0.1}
                            emissive={pressed === "base" && isBase ? BASE_PRESSED : "#000000"}
                            emissiveIntensity={pressed === "base" && isBase ? 0.45 : 0}
                            {...handleMaterialProps}
                        />
                    </mesh>
                );
            })}
        </group>
    );
};

export default DetailCornerHandles;
